import React, { useState, useEffect } from 'react';
import { COUNTRIES } from '../utils/constants';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function ProbationExpiriesTab({ user }) {
  const [expiries, setExpiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExpiries();
  }, []);

  const fetchExpiries = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/probation_expiries?days_ahead=365`, {
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch expiries');
      const data = await res.json();
      setExpiries(data.expiries || []);
    } catch (error) {
      console.error('Error fetching expiries:', error);
      setExpiries([]);
    } finally {
      setLoading(false);
    }
  };

  const getCountryLabel = (countryCode) => {
    const country = COUNTRIES.find(c => c.value === countryCode);
    return country ? country.label : countryCode;
  };

  const getDaysUntilExpiryColor = (days) => {
    if (days <= 7) return '#dc2626'; // red
    if (days <= 14) return '#ea580c'; // orange
    if (days <= 30) return '#d97706'; // amber
    return '#059669'; // green
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: 'var(--symbolica-muted)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid var(--symbolica-black)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading probation expiries...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 className="content-title" style={{ marginBottom: '0.5rem' }}>
          Probation Expiries Report
        </h2>
        <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
          Track upcoming probation expiries to ensure managers are notified in advance for performance reviews.
        </p>
      </div>


      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '600', color: '#dc2626', marginBottom: '0.5rem' }}>
            {expiries.filter(e => e.days_until_expiry <= 7).length}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)' }}>Expiring in 7 days</div>
        </div>
        
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '600', color: '#ea580c', marginBottom: '0.5rem' }}>
            {expiries.filter(e => e.days_until_expiry > 7 && e.days_until_expiry <= 14).length}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)' }}>Expiring in 14 days</div>
        </div>
        
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '600', color: '#d97706', marginBottom: '0.5rem' }}>
            {expiries.filter(e => e.days_until_expiry > 14 && e.days_until_expiry <= 30).length}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)' }}>Expiring in 30 days</div>
        </div>
        
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2rem', fontWeight: '600', color: '#059669', marginBottom: '0.5rem' }}>
            {expiries.filter(e => e.days_until_expiry > 30).length}
          </div>
          <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)' }}>Expiring later</div>
        </div>
      </div>

      {/* Detailed List */}
      {expiries.length === 0 ? (
        <div style={{
          background: '#F6F4F0',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}>⏰</div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--symbolica-black)' }}>
            No probation expiries
          </h3>
          <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
            No employees have probation expiring in the future.
          </p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#F6F4F0',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)', margin: 0 }}>
              Upcoming Expiries ({expiries.length})
            </h3>
          </div>
          
          <div style={{ padding: '0' }}>
            {expiries.map((expiry, index) => (
              <div
                key={expiry.email}
                style={{
                  padding: '1.5rem',
                  borderBottom: index < expiries.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: 'var(--symbolica-black)', 
                      margin: 0 
                    }}>
                      {expiry.first_name} {expiry.last_name}
                    </h4>
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: 'var(--symbolica-warm-border)',
                      color: 'var(--symbolica-muted)'
                    }}>
                      {expiry.title}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                    <div>
                      <strong>Country:</strong> {getCountryLabel(expiry.country)}
                    </div>
                    <div>
                      <strong>Manager:</strong> {expiry.manager_email}
                    </div>
                    <div>
                      <strong>Started:</strong> {new Date(expiry.probation_start_date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontSize: '14px', 
                    color: 'var(--symbolica-muted)', 
                    marginBottom: '4px' 
                  }}>
                    Probation Ends
                  </div>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: getDaysUntilExpiryColor(expiry.days_until_expiry),
                    marginBottom: '4px'
                  }}>
                    {new Date(expiry.probation_end_date).toLocaleDateString()}
                  </div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: getDaysUntilExpiryColor(expiry.days_until_expiry),
                    fontWeight: '500'
                  }}>
                    {expiry.days_until_expiry} days
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
